import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { boot, saveSession, type BootState } from '../boot';
import { SessionProvider } from './session';
import { TabBar } from './TabBar';
import { AppHeader } from './AppHeader';
import { InstallBanner } from '../components/InstallBanner';
import { RoomDock } from '@kilroy/room';

import { FieldDay } from '../screens/FieldDay';
import { Capture } from '../screens/Capture';
import { Assigned } from '../screens/Assigned';
import { AssignmentScreen } from '../screens/AssignmentScreen';
import { EvidenceDetail } from '../screens/EvidenceDetail';
import { Priorities } from '../screens/Priorities';
import { MapScreen } from '../screens/MapScreen';
import { Settings } from '../screens/Settings';
import { Onboarding } from '../screens/Onboarding';
import { SignIn } from '../screens/SignIn';

export function App() {
  const [state, setState] = useState<BootState | null>(null);
  const location = useLocation();

  useEffect(() => {
    void boot().then(setState);
  }, []);

  // Deliberately blank rather than a spinner. Boot is a few milliseconds of
  // IndexedDB; a spinner that flashes for 40ms reads as slowness that isn't
  // there.
  if (!state) return <div className="boot" aria-busy="true" />;

  /* Sign-in comes before onboarding.
   *
   * Onboarding explains how the app behaves for *you* — your assignments, your
   * records — and it cannot say any of that truthfully without knowing who you
   * are. Identity first, then the tour. */
  if (!state.session) {
    return (
      <SignIn
        onSignedIn={async (session) => {
          await saveSession(session);
          setState({ ...state, session, userId: session.userId });
        }}
      />
    );
  }

  if (!state.onboarded) {
    return (
      <SessionProvider value={state}>
        <Onboarding onDone={() => setState({ ...state, onboarded: true })} />
      </SessionProvider>
    );
  }

  // The map owns its whole viewport; every other screen scrolls under the
  // pill and above the tab bar.
  const fullBleed = location.pathname.startsWith('/map');

  return (
    <SessionProvider value={state}>
      <div className={`shell${fullBleed ? ' shell--bleed' : ''}`}>
        <AppHeader />
        {!state.standalone && <InstallBanner />}

        <main className="shell__body scroll" id="main">
          <Routes>
            <Route path="/" element={<FieldDay />} />
            <Route path="/capture" element={<Capture />} />
            <Route path="/assigned" element={<Assigned />} />
            <Route path="/assignment/:id" element={<AssignmentScreen />} />
            <Route path="/evidence/:id" element={<EvidenceDetail />} />
            <Route path="/priorities" element={<Priorities />} />
            <Route path="/map" element={<MapScreen />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <TabBar />
        {/* Outside <main> so it never sits inside a scroll container, and last
            so it paints above the tab bar. Renders nothing without a token. */}
        <RoomDock surface="field" />
      </div>
    </SessionProvider>
  );
}
